import { createApp } from './vendor/petite-vue.min.js'
import { useInputSelect, getData, getCounties, useFormRules, useAnchorToError } from './modules/_form.js'

try {
  (async () => {
		const data = await getData()
    const counties = await getCounties()
    const modelList = data.products.guitars.map(item => ({
      value: item.model,
      label: item.model,
    }))
    const countyList = counties.map(item => ({
      value: item.name,
      label: item.name,
    }))
    const materialList = data.customize.materials.map(item => ({
      value: item.name,
      label: item.name,
      image: item.image,
      price: item.price,
    }))
    const patternList = data.customize.patterns.map(item => ({
      value: item.name,
      label: item.name,
      image: item.image,
      price: item.price,
    }))
    const positionList = data.customize.positions.map(item => ({
      value: item.name,
      label: item.name,
      image: item.image,
    }))

    const { required, checkPhone, checkEmail, checkAddress } = useFormRules()

    function checkModel(value) {
      return modelList.some(item => item.value === value)
    }

    function checkMaterial(value) {
      return value && materialList.some(item => item.value === value)
    }

    function checkPattern(value) {
      return value && patternList.some(item => item.value === value)
    }

    function checkPosition(value) {
      return value && positionList.some(item => item.value === value)
    }

    function checkRemark(value) {
      return this.position !== positionList.at(-1).label || required(value)
    }

		createApp({
      // customize info
			model: '',
      modelList,
      get filteredModelList() {
        return this.modelList.filter(item => item.label.toLowerCase().includes(this.model.toLowerCase()))
      },
      onSelectModel(model) {
        this.model = model.value
      },
      material: '',
      materialList,
      pattern: '',
      patternList,
      position: '',
      positionList,
      toaster: false,
      toasterMessage: '',
      files: [],
      images: [],
      onImageUpload(event) {
        const maxLength = 3
        const files = event.target.files
        const filesArray = Array.from(files)
        const newFiles = filesArray.filter(file => file.size <= 1024 * 1024)
        const newImages = newFiles.map(file => ({
          url: URL.createObjectURL(file),
          name: file.name
        }))
        const readyFiles = [...this.files, ...newFiles]
        this.files = readyFiles.filter((_, index) => index < maxLength)
        this.images = [...this.images, ...newImages].filter((_, index) => index < maxLength)
        this.setUploadInput()
        if (filesArray.length !== newFiles.length || readyFiles.length > maxLength) {
          const overSizeFiles = filesArray.filter(file => file.size > 1024 * 1024)
          const overSizeMessage = filesArray.length !== newFiles.length ? `${overSizeFiles.map(file => file.name).join('、')} 無法上傳，檔案大小超過 1MB` : ''
          const overLengthMessage = readyFiles.length > maxLength ? `最多只能上傳 ${maxLength} 張圖片` : ''
          this.toaster = true
          this.toasterMessage = `${[overSizeMessage, overLengthMessage].filter(Boolean).join('，')}`
          setTimeout(() => {
            this.toaster = false
          }, 4000)
        }
      },
      onRemoveImage(index) {
        URL.revokeObjectURL(this.images[index].url)
        this.files.splice(index, 1)
        this.images.splice(index, 1)
        this.setUploadInput()
      },
      setUploadInput() {
        const dataTransfer = new DataTransfer()
        this.files.forEach(file => dataTransfer.items.add(file))
        document.getElementById('upload').files = dataTransfer.files
      },
      get priceArray() {
        const keys = ['material', 'pattern', 'position']
        return keys.map((key) => {
          return this[key] ? (this[`${key}List`].find(item => item.value === this[key])?.price || '0') : '0'
        })
      },
      get totalPrice() {
        const prices = this.priceArray
        const hasInterval = prices.some(item => item.includes('-'))
        const minPrice = prices.reduce((prev, curr) => {
          return prev + parseInt(curr.replace('up', '').split('-')[0])
        }, 0)
        const maxPrice = prices.reduce((prev, curr) => {
          return prev + parseInt(curr.replace('up', '').split('-')[1] || curr)
        }, 0)

        return `${this.formatPrice(minPrice)}${hasInterval ? ` - ${this.formatPrice(maxPrice)}` : ''}`
      },
      get hasUpPrice() {
        return this.priceArray.some(item => item.includes('up'))
      },
      setTotalPrice(e) {
        e.target.value = this.totalPrice
      },
      // contact info
      name: '',
      phone: '',
      email: '',
      snsType: 'LINE',
      snsTypeList: ['LINE', 'Facebook', 'Instagram','WeChat'],
      snsId: '',
      county: '',
      countyList,
      area: '',
      get areaList() {
        return counties.find(item => item.name === this.county)?.areas.map(item => ({
          value: item.name,
          label: item.name,
        })) || []
      },
      address: '',
      remark: '',
      // validate
      rules: {
        model: { required, checkModel },
        material: { checkMaterial },
        pattern: { checkPattern },
        position: { checkPosition },
        name: { required },
        phone: { required, checkPhone },
        email: { required, checkEmail },
        address: { checkAddress },
        remark: { checkRemark },
      },
      errors: {},
      errorMessages: {
        checkModel: '請輸入正確的吉他型號',
        checkMaterial: '請選擇鑲嵌材質',
        checkPattern: '請選擇鑲嵌圖樣',
        checkPosition: '請選擇鑲嵌位置',
        required: '此欄位為必填',
        checkPhone: '請輸入正確的手機號碼，例如：0912345678',
        checkEmail: '請輸入正確的電子郵件，例如：example@example.com',
        checkAddress: '請輸入完整地址',
        checkRemark: '因為鑲嵌位置選擇其他，請輸入補充說明',
      },
      isValidated: false,
      setErrors() {
        this.errors = this.isValidated ?
          Object.entries(this.rules)
          .reduce((acc, [key, rules]) => {
            return {
              ...acc,
              [key]: Object.entries(rules).reduce((prev, [ruleName, fn]) => {
                if (!fn.call(this, this[key])) {
                  prev.push(ruleName)
                }
                return prev
              }, [])
            }
          }, {}) :
          {}
          // console.log(this.errors)
      },
      useValidate() {
        this.isValidated = true
        this.setErrors()
        return Object.values(this.errors).every(item => item.length === 0)
      },
      useSubmit() {
        const isAllCorrect = this.useValidate()

        if (!isAllCorrect) {
          setTimeout(() => {
            useAnchorToError()
          }, 100)
        }
      },
      // format price
      formatPrice(price) {
        if (typeof price === 'number') {
          return price.toLocaleString()
        }
        if (price.includes('up')) {
          return `${parseInt(price.split('up')[0]).toLocaleString()} up`
        }
        if (price.includes('-')) {
          const [min, max] = price.split('-')
          return `${parseInt(min).toLocaleString()} - ${parseInt(max).toLocaleString()}`
        }
        return parseInt(price).toLocaleString()
      },
      // mounted
			onMounted() {
        useInputSelect()
			}
		}).mount('#app')
	})()
}
catch(error) {
	console.error(error)
}